import React, { useState } from "react";
import {
  Box,
  Container,
  Heading,
  Text,
  SimpleGrid,
  Badge,
  HStack,
  VStack,
  Flex,
  Tag,
  Divider,
  Input,
  InputGroup,
  InputLeftElement,
  Select,
} from "@chakra-ui/react";
import { SearchIcon } from "@chakra-ui/icons";
import { useTranslation } from "react-i18next";
import SEO from "./SEO";
import { ZODIAC_SIGNS, ELEMENT_COLORS, ZodiacElement, ZodiacSign } from "../data/zodiacData";
import { getZodiacCopy, getZodiacList } from "../i18n/content";

const ELEMENTS: ZodiacElement[] = ["fire", "earth", "air", "water"];

const ZodiacCard: React.FC<{ sign: ZodiacSign }> = ({ sign }) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);

  const colors = ELEMENT_COLORS[sign.element];
  const name        = getZodiacCopy(t, sign.id, "name");
  const dateRange   = getZodiacCopy(t, sign.id, "dateRange");
  const element     = t(`zodiac.elements.${sign.element}.label`);
  const planet      = getZodiacCopy(t, sign.id, "rulingPlanet");
  const description = getZodiacCopy(t, sign.id, "description");
  const traits      = getZodiacList(t, sign.id, "traits");
  const strengths   = getZodiacList(t, sign.id, "strengths");
  const weaknesses  = getZodiacList(t, sign.id, "weaknesses");
  const luckyColor  = getZodiacCopy(t, sign.id, "luckyColor");
  const compatible  = getZodiacList(t, sign.id, "compatibleWith");

  return (
    <Box
      bg="white"
      borderRadius="2xl"
      overflow="hidden"
      boxShadow="sm"
      border="1px solid"
      borderColor="gray.100"
      transition="all 0.25s ease"
      _hover={{ boxShadow: "md", borderColor: sign.accentColor }}
    >
      {/* Header */}
      <Box bg={sign.cardColor} px={5} pt={5} pb={4}>
        <Flex align="center" gap={3}>
          <Box
            w="56px" h="56px"
            borderRadius="full"
            bg="white"
            display="flex" alignItems="center" justifyContent="center"
            fontSize="28px"
            boxShadow="sm"
            flexShrink={0}
          >
            {sign.symbol}
          </Box>
          <Box flex={1} minW={0}>
            <Heading size="md" color="gray.800" noOfLines={1}>{name}</Heading>
            <Text fontSize="xs" color="gray.500" mt={0.5}>{dateRange}</Text>
          </Box>
          <Badge
            px={2} py={1}
            borderRadius="full"
            bg={colors.badge}
            color={colors.text}
            fontSize="xs"
            fontWeight="semibold"
            flexShrink={0}
          >
            {element}
          </Badge>
        </Flex>
      </Box>

      {/* Body */}
      <VStack align="stretch" spacing={3} px={5} py={4}>

        {/* Ruler / Lucky Color / Lucky Number */}
        <HStack justify="space-between" flexWrap="wrap" gap={2}>
          <HStack spacing={1}>
            <Text fontSize="xs" color="gray.400">{t("zodiac.card.ruler")}</Text>
            <Text fontSize="xs" fontWeight="semibold" color="gray.700">{planet}</Text>
          </HStack>
          <HStack spacing={1}>
            <Text fontSize="xs" color="gray.400">{t("zodiac.card.luckyColor")}</Text>
            <Text fontSize="xs" fontWeight="semibold" color="gray.700">{luckyColor}</Text>
          </HStack>
          <HStack spacing={1}>
            <Text fontSize="xs" color="gray.400">{t("zodiac.card.luckyNumber")}</Text>
            <Text fontSize="xs" fontWeight="semibold" color="gray.700">{sign.luckyNumbers.join(", ")}</Text>
          </HStack>
        </HStack>

        <Divider />

        {/* Description */}
        <Box>
          <Text fontSize="xs" color="gray.600" lineHeight="1.8" noOfLines={expanded ? undefined : 3}>
            {description}
          </Text>
          <Text
            as="button"
            fontSize="xs"
            color={colors.text}
            fontWeight="semibold"
            mt={1}
            onClick={() => setExpanded((v) => !v)}
            _hover={{ opacity: 0.75 }}
          >
            {expanded ? t("zodiac.card.showLess") : t("zodiac.card.readMore")}
          </Text>
        </Box>

        <Divider />

        {/* Traits */}
        <Box>
          <Text fontSize="xs" color="gray.400" mb={1.5} fontWeight="medium">
            {t("zodiac.card.traits")}
          </Text>
          <Flex flexWrap="wrap" gap={1.5}>
            {traits.map((trait) => (
              <Tag
                key={trait}
                size="sm"
                bg={sign.cardColor}
                color={colors.text}
                borderRadius="full"
                fontWeight="medium"
                fontSize="xs"
              >
                {trait}
              </Tag>
            ))}
          </Flex>
        </Box>

        {/* Strengths */}
        <Box>
          <Text fontSize="xs" color="gray.400" mb={1.5} fontWeight="medium">
            {t("zodiac.card.strengths")}
          </Text>
          <VStack align="stretch" spacing={1}>
            {strengths.map((s) => (
              <Text key={s} fontSize="xs" color="gray.600" lineHeight="1.6">· {s}</Text>
            ))}
          </VStack>
        </Box>

        {/* Weaknesses */}
        <Box>
          <Text fontSize="xs" color="gray.400" mb={1.5} fontWeight="medium">
            {t("zodiac.card.weaknesses")}
          </Text>
          <VStack align="stretch" spacing={1}>
            {weaknesses.map((w) => (
              <Text key={w} fontSize="xs" color="gray.600" lineHeight="1.6">· {w}</Text>
            ))}
          </VStack>
        </Box>

        <Divider />

        {/* Best Match */}
        <Box>
          <Text fontSize="xs" color="gray.400" mb={1.5} fontWeight="medium">
            {t("zodiac.card.bestMatch")}
          </Text>
          <HStack spacing={2} flexWrap="wrap">
            {compatible.map((c) => (
              <Badge
                key={c}
                px={2} py={0.5}
                borderRadius="full"
                bg={colors.badge}
                color={colors.text}
                fontSize="xs"
                fontWeight="medium"
              >
                {c}
              </Badge>
            ))}
          </HStack>
        </Box>

      </VStack>
    </Box>
  );
};

const Zodiac: React.FC = () => {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");
  const [elementFilter, setElementFilter] = useState<string>("all");

  const filtered = ZODIAC_SIGNS.filter((sign) => {
    const name = getZodiacCopy(t, sign.id, "name");
    const matchSearch = name.toLowerCase().includes(search.toLowerCase());
    const matchElement = elementFilter === "all" || sign.element === elementFilter;
    return matchSearch && matchElement;
  });

  return (
    <>
      <SEO
        title={t("zodiac.seo.title")}
        description={t("zodiac.seo.description")}
        keywords={t("zodiac.seo.keywords")}
        canonical="https://deepsoulai.com/zodiac"
      />

      {/* Hero Banner */}
      <Box bgGradient="linear(to-br, blue.50, purple.50, pink.50)" pt={{ base: 10, md: 14 }} pb={{ base: 8, md: 12 }}>
        <Container maxW="1100px">
          <VStack spacing={3} textAlign="center">
            <Text fontSize={{ base: "3xl", md: "4xl" }}>✨</Text>
            <Heading
              as="h1"
              size={{ base: "xl", md: "2xl" }}
              bgGradient="linear(to-r, purple.500, pink.500)"
              bgClip="text"
              fontWeight="extrabold"
            >
              {t("zodiac.title")}
            </Heading>
            <Text color="gray.600" maxW="540px" fontSize={{ base: "sm", md: "md" }} lineHeight="tall">
              {t("zodiac.subtitle")}
            </Text>

            {/* Element Legend */}
            <Flex mt={4} gap={3} flexWrap="wrap" justify="center">
              {ELEMENTS.map((el) => {
                const colors = ELEMENT_COLORS[el];
                return (
                  <Box
                    key={el}
                    px={4} py={2.5}
                    bg="white"
                    borderRadius="xl"
                    boxShadow="sm"
                    border="1px solid"
                    borderColor={colors.badge}
                    textAlign="center"
                    minW="120px"
                  >
                    <Text fontSize="sm" fontWeight="bold" color={colors.text}>
                      {t(`zodiac.elements.${el}.label`)}
                    </Text>
                    <Text fontSize="xs" color="gray.500" mt={0.5}>
                      {t(`zodiac.elements.${el}.desc`)}
                    </Text>
                  </Box>
                );
              })}
            </Flex>
          </VStack>
        </Container>
      </Box>

      {/* Filter Bar */}
      <Box bg="white" py={4} borderBottom="1px solid" borderColor="gray.100" position="sticky" top="64px" zIndex={5}>
        <Container maxW="1100px">
          <Flex gap={3} flexWrap="wrap" align="center">
            <InputGroup maxW="260px" size="sm">
              <InputLeftElement pointerEvents="none">
                <SearchIcon color="gray.400" boxSize={3} />
              </InputLeftElement>
              <Input
                placeholder={t("zodiac.search")}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                borderRadius="full"
                bg="gray.50"
                border="1px solid"
                borderColor="gray.200"
                _focus={{ borderColor: "purple.400", boxShadow: "0 0 0 1px var(--chakra-colors-purple-400)" }}
              />
            </InputGroup>
            <Select
              size="sm"
              maxW="220px"
              borderRadius="full"
              bg="gray.50"
              border="1px solid"
              borderColor="gray.200"
              value={elementFilter}
              onChange={(e) => setElementFilter(e.target.value)}
              _focus={{ borderColor: "purple.400" }}
            >
              <option value="all">{t("zodiac.filterAll")}</option>
              {ELEMENTS.map((el) => (
                <option key={el} value={el}>{t(`zodiac.elements.${el}.filter`)}</option>
              ))}
            </Select>
            <Text fontSize="sm" color="gray.400" ml="auto">
              {filtered.length} / 12
            </Text>
          </Flex>
        </Container>
      </Box>

      {/* Grid */}
      <Box bg="gray.50" minH="60vh" py={{ base: 8, md: 12 }}>
        <Container maxW="1100px">
          {filtered.length === 0 ? (
            <VStack py={20} spacing={3}>
              <Text fontSize="3xl">🔍</Text>
              <Text color="gray.400">{t("zodiac.noResult")}</Text>
            </VStack>
          ) : (
            <SimpleGrid columns={{ base: 1, sm: 2, lg: 3 }} spacing={5}>
              {filtered.map((sign) => (
                <ZodiacCard key={sign.id} sign={sign} />
              ))}
            </SimpleGrid>
          )}
        </Container>
      </Box>
    </>
  );
};

export default Zodiac;
